import { z } from 'zod';
import { assignmentStatusSchema } from '../assignments.js';
import { conditionSchema, ownershipSchema } from './catalog.js';
/**
 * Issuance (REQUIREMENTS §2.4 `issuanceForms` / `assignments`, §2.5 "Issue").
 *
 * One form is the batch a soldier signs as one act. Each line becomes one
 * assignment — the live link between the soldier and the thing. Both are
 * created together, pending signature, inside one Firestore transaction that
 * also reserves the stock; nothing here is ever written from a browser.
 */
export const formStatusSchema = z.enum([
    'pending_signature',
    'signed',
    'rejected',
    'cancelled',
    'expired',
    /**
     * A form completed by admin/csm attestation instead of the
     * soldier's signature — a transfer (§3.2) or, since story 25, a plain issue.
     * Distinct from `signed` on purpose: the soldier did not sign, and every
     * screen says so.
     */
    'attested',
]);
/**
 * A line as the issuer submits it. Serialized items name the unit; quantity
 * items name how many. The server decides which is which from the catalog
 * and refuses a mismatch.
 */
export const issueLineRequestSchema = z.object({
    catalogItemId: z.string().min(1),
    inventoryUnitId: z.string().min(1).nullable().default(null),
    quantity: z.number().int().min(1).max(10_000).default(1),
    condition: conditionSchema.default('good'),
    notes: z.string().trim().max(500).nullable().default(null),
});
export const issueRequestSchema = z.object({
    soldierUid: z.string().min(1),
    lines: z.array(issueLineRequestSchema).min(1).max(50),
    missionId: z.string().min(1).nullable().default(null),
    /** ISO date. Null means "standing gear", no return expected by a date. */
    dueDate: z.string().datetime().nullable().default(null),
});
/**
 * POST /api/inventory/units/:id/issue — issue *this specific* unheld unit to
 * a soldier named by personal number (story 43): started from the registry
 * instead of the usual soldier-first wizard. The receiver is named by
 * personal number, the same as a transfer (§3.7: no browsing the roster).
 */
export const issueUnitRequestSchema = z.object({
    toPersonalNumber: z.string().trim().min(1).max(32),
    dueDate: z.string().datetime().nullable().default(null),
});
/**
 * A line as stored on the form. Names and serial are denormalised at issue
 * time: the form is what the soldier reads before signing, and a catalog
 * rename next year must not change what it says they saw.
 */
export const formLineSchema = z.object({
    catalogItemId: z.string().min(1),
    inventoryUnitId: z.string().nullable(),
    quantity: z.number().int().min(1),
    condition: conditionSchema,
    notes: z.string().nullable(),
    nameHe: z.string(),
    nameEn: z.string(),
    serialNumber: z.string().nullable(),
    ownership: ownershipSchema,
    /** Ammunition and the like: expended, never returned (§2.5). */
    consumable: z.boolean(),
});
/**
 * An admin/csm attestation: the *attester's* own drawn signature
 * and a mandatory comment, standing in for a signature the soldier did not
 * make. Never stored under `signatures/`, never called one.
 */
export const attestationSchema = z.object({
    byUid: z.string().min(1),
    byName: z.string(),
    comment: z.string(),
    /** The attester's own drawn signature, in Storage. Never the soldier's. */
    imagePath: z.string(),
    at: z.string().nullable(),
});
export const issuanceFormSchema = z.object({
    id: z.string().min(1),
    soldierUid: z.string().min(1),
    teamId: z.string().nullable(),
    platoonId: z.string().nullable(),
    status: formStatusSchema,
    lineItems: z.array(formLineSchema),
    missionId: z.string().nullable(),
    /** Set when this form issues a rotation replacement; pairs it with the old assignment (§2.5). */
    rotationId: z.string().nullable().default(null),
    /** Set when this form hands an item over from another soldier (§3.2). */
    transferId: z.string().nullable().default(null),
    /**
     * Set when this form distributes gear from a platoon sergeant's own
     * holdings rather than from stock (story 30): the sergeant's uid. Stock
     * does not move; the sergeant stays accountable to the company store.
     */
    custodianUid: z.string().nullable().default(null),
    issuedBy: z.string().min(1),
    issuedAt: z.string().nullable(),
    dueDate: z.string().nullable(),
    signatureId: z.string().nullable(),
    signedAt: z.string().nullable(),
    soldierComment: z.string().nullable(),
    rejectedReason: z.string().nullable(),
    /** Denormalised for the list screens; the uids are what the ledger keeps. */
    soldierName: z.string(),
    issuerName: z.string(),
    /** Set when the storeroom changed the lines before signature (owner, 2026-09-13). */
    editedAt: z.string().nullable().default(null),
    editedBy: z.string().nullable().default(null),
    /**
     * Present only on an `attested` form: who completed it under their own
     * signature, and why. `signatureId` is null then, always.
     */
    attestation: attestationSchema.nullable().default(null),
});
export const formResponseSchema = z.object({ form: issuanceFormSchema });
export const formsResponseSchema = z.object({ forms: z.array(issuanceFormSchema) });
/** GET /api/forms — scoped to the caller; a soldier sees only their own. */
export const formsQuerySchema = z.object({
    status: formStatusSchema.optional(),
    soldierUid: z.string().min(1).optional(),
    /** A team or platoon: every form for a soldier in it. */
    unitId: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
/**
 * POST /api/forms/:id/cancel — the issuer, or a company-wide role, withdraws
 * a form that has not been signed. Stock is released, units go back to
 * in_stock, and the assignments close as `cancelled`. A signed form cannot be
 * cancelled: corrections happen by cancelling and re-issuing, and both remain
 * visible in the ledger (§2.5).
 */
export const cancelFormRequestSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
/**
 * PATCH /api/forms/:id/lines — admin or csm replace the lines of
 * a form that has not been signed (owner, 2026-09-13: "the soldier doesn't
 * need one or more items, or a different amount"). The old reservation is
 * released and the new one taken in the same transaction; the soldier is
 * told and signs the corrected form. Only a plain issue can be edited — a
 * transfer or rotation form carries a pairing that the lines are part of.
 */
export const editFormLinesRequestSchema = z.object({
    lines: z.array(issueLineRequestSchema).min(1).max(50),
    reason: z.string().trim().max(500).nullable().default(null),
});
/**
 * POST /api/forms/:id/attest — admin or csm complete an unsigned
 * plain issue without the soldier's signature (owner, 2026-09-13, story 25:
 * "no phone at the moment"). The image is the *caller's* own signature and
 * the comment is mandatory; the form goes `attested`, never `signed`, and
 * the assignments go active with `signatureId: null`. This is the §3.2
 * transfer override applied to an issue — an attestation, not a signature on
 * the soldier's behalf, which nothing in this system can produce.
 * A transfer form is refused here: its override lives on the transfer.
 */
export const attestFormRequestSchema = z.object({
    comment: z.string().trim().min(3).max(1000),
    imageDataUrl: z.string().regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/),
});
export const assignmentSchema = z.object({
    id: z.string().min(1),
    formId: z.string().min(1),
    soldierUid: z.string().min(1),
    teamId: z.string().nullable(),
    platoonId: z.string().nullable(),
    catalogItemId: z.string().min(1),
    inventoryUnitId: z.string().nullable(),
    quantity: z.number().int().min(0),
    ownership: ownershipSchema,
    status: assignmentStatusSchema,
    missionId: z.string().nullable(),
    /**
     * Shared by the old and the replacement assignment of one rotation, so the
     * pair reads as one event (§2.5 "Rotation / swap"). Null otherwise.
     */
    rotationId: z.string().nullable().default(null),
    /** The transfer this assignment came from, or that is pending on it (§3.2). */
    transferId: z.string().nullable().default(null),
    /**
     * Platoon store (story 30). On a soldier's assignment that came from a
     * platoon sergeant's holdings: the sergeant's assignment it was carved
     * out of, and the sergeant's uid. Returns go back to that holding, not to
     * stock.
     */
    sourceAssignmentId: z.string().nullable().default(null),
    custodianUid: z.string().nullable().default(null),
    /**
     * On a platoon sergeant's own assignment: how much of `quantity` is
     * currently out with soldiers (pending or active). A serialized unit is
     * 0 or 1. The holding cannot be returned, transferred or rotated while
     * this is above zero.
     */
    distributedQuantity: z.number().int().min(0).default(0),
    issuedBy: z.string().min(1),
    issuedAt: z.string().nullable(),
    dueDate: z.string().nullable(),
    signatureId: z.string().nullable(),
    signedAt: z.string().nullable(),
    returnCondition: conditionSchema.nullable(),
    closedAt: z.string().nullable(),
    notes: z.string().nullable(),
    nameHe: z.string(),
    nameEn: z.string(),
    serialNumber: z.string().nullable(),
    /** Frozen from the catalog at issue time: decides expend vs return (§2.5). */
    consumable: z.boolean().default(false),
    /** Consumables: how much of `quantity` has been used so far (§2.5). */
    expendedQuantity: z.number().int().min(0).default(0),
    /** Live from the catalog, not frozen: whether a transfer may be started on this (§3.2, owner 2026-09-13). */
    transferable: z.boolean().default(true),
    /**
     * Present only on an assignment that went active by admin attestation
     * rather than the soldier's signature (§3.2). `signatureId` is null then.
     */
    attestation: attestationSchema.nullable().default(null),
    /** The open (or last) loss/damage report on this assignment, if any. */
    report: z
        .object({
        kind: z.enum(['lost', 'damaged']),
        notes: z.string(),
        photoPath: z.string().nullable(),
        filedBy: z.string(),
        filedAt: z.string().nullable(),
        closedBy: z.string().nullable().default(null),
        closedAt: z.string().nullable().default(null),
        outcome: z.enum(['found', 'written_off']).nullable().default(null),
        closingNotes: z.string().nullable().default(null),
    })
        .nullable()
        .default(null),
});
//# sourceMappingURL=issuance.js.map